/**
 * CoachProfile — public, SEO-indexable page at `/coach/:slug`.
 *
 * Displays a coach's bio, stats, social links and their published plays in the
 * Plays Hub. No auth required. Renders 404 if the coach hasn't enabled their
 * public profile. Fully translated EN/ES/FR with hreflang annotations.
 */
import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import axios from 'axios';
import { Heart, Eye, Globe, Twitter, Instagram, ArrowLeft, BookOpen, Award } from 'lucide-react';
import { useLang } from '@/context/LangContext';

const API = process.env.REACT_APP_BACKEND_URL;
const LANG_LOCALE = { es: 'es-ES', en: 'en-US', fr: 'fr-FR' };
const OG_LOCALE = { es: 'es_ES', en: 'en_US', fr: 'fr_FR' };
const PLAYS_PATH = { es: '/jugadas', en: '/plays', fr: '/jeux' };

const fmt = (n, lang) => Number(n || 0).toLocaleString(LANG_LOCALE[lang] || 'es-ES');

const absImg = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  return `${API}${url}`;
};

function StatPill({ icon: Icon, label, value, accent = 'text-gold', testid }) {
  return (
    <div className="rounded-xl border border-gold/20 bg-navy-deep px-4 py-3 min-w-[120px]" data-testid={testid}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.3em] text-slate-400">
        <Icon className="w-3 h-3" /> {label}
      </div>
      <div className={`mt-1 font-h text-2xl tabular-nums ${accent}`}>{value}</div>
    </div>
  );
}

function PlayCard({ play, lang }) {
  const playsBase = PLAYS_PATH[lang] || '/jugadas';
  return (
    <Link
      to={`${playsBase}/${play.slug}`}
      data-testid={`coach-play-${play.slug}`}
      className="group block rounded-xl border border-gold/15 bg-navy-deep overflow-hidden hover:border-gold/60 transition-all"
    >
      <div className="relative aspect-[16/10] bg-gradient-to-br from-navy to-navy-deep">
        {play.image_partial_url ? (
          <img src={absImg(play.image_partial_url)} alt={play.title} loading="lazy" className="w-full h-full object-cover opacity-90" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-gold/40 font-h text-3xl uppercase tracking-widest">{play.category}</div>
          </div>
        )}
        <div className="absolute top-2 right-2 px-2 py-1 rounded-md bg-black/60 backdrop-blur text-[10px] font-mono uppercase tracking-widest text-gold border border-gold/30">
          {play.category}
        </div>
      </div>
      <div className="p-3">
        <h3 className="font-h text-base leading-tight text-white group-hover:text-gold transition-colors line-clamp-2">{play.title}</h3>
        <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-400">
          <span className="inline-flex items-center gap-1"><Heart className="w-3 h-3 text-rose-400" /> {fmt(play.likes_count, lang)}</span>
          <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3 text-slate-300" /> {fmt(play.views_count, lang)}</span>
        </div>
      </div>
    </Link>
  );
}

export default function CoachProfile() {
  const { slug } = useParams();
  const { lang, t } = useLang();
  const [coach, setCoach] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [notFound, setNotFound] = React.useState(false);

  React.useEffect(() => {
    setLoading(true);
    axios.get(`${API}/api/coach/public/${slug}`)
      .then((r) => setCoach(r.data))
      .catch((e) => { if (e?.response?.status === 404) setNotFound(true); })
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen bg-navy flex items-center justify-center text-gold font-h tracking-widest">{t('cp_loading')}</div>;
  }

  const playsBase = PLAYS_PATH[lang] || '/jugadas';

  if (notFound || !coach) {
    return (
      <div className="min-h-screen bg-navy text-white flex flex-col items-center justify-center px-4">
        <Helmet><title>{`${t('cp_404_title')} | PlayMaster IA`}</title><meta name="robots" content="noindex" /></Helmet>
        <h1 className="font-h text-4xl uppercase tracking-tight text-gold">404</h1>
        <p className="text-slate-400 mt-2">{t('cp_404_body')}</p>
        <Link to={playsBase} className="mt-6 inline-flex items-center gap-2 text-gold hover:underline">
          <ArrowLeft className="w-4 h-4" /> {t('cp_back_to_plays')}
        </Link>
      </div>
    );
  }

  const seoDesc = coach.bio && coach.bio.length > 30
    ? coach.bio.slice(0, 160)
    : t('cp_seo_default_desc', { name: coach.name, count: coach.stats.plays });

  const ldPerson = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    name: coach.name,
    jobTitle: 'Basketball Coach',
    url: `https://playmasteria.com/coach/${coach.public_slug}`,
    image: coach.avatar_url || undefined,
    description: coach.bio || undefined,
  };

  return (
    <div className="min-h-screen bg-navy text-white" data-testid="coach-profile-page">
      <Helmet>
        <title>{`${coach.name} — ${t('cp_label_coach')} | PlayMaster IA`}</title>
        <meta name="description" content={seoDesc} />
        <link rel="canonical" href={`https://playmasteria.com/coach/${coach.public_slug}`} />
        <link rel="alternate" hrefLang="es" href={`https://playmasteria.com/coach/${coach.public_slug}`} />
        <link rel="alternate" hrefLang="en" href={`https://playmasteria.com/coach/${coach.public_slug}`} />
        <link rel="alternate" hrefLang="fr" href={`https://playmasteria.com/coach/${coach.public_slug}`} />
        <link rel="alternate" hrefLang="x-default" href={`https://playmasteria.com/coach/${coach.public_slug}`} />
        <meta property="og:title" content={`${coach.name} — ${t('cp_label_coach')}`} />
        <meta property="og:description" content={seoDesc} />
        <meta property="og:type" content="profile" />
        <meta property="og:locale" content={OG_LOCALE[lang] || 'es_ES'} />
        {coach.avatar_url && <meta property="og:image" content={coach.avatar_url} />}
        <script type="application/ld+json">{JSON.stringify(ldPerson)}</script>
      </Helmet>

      {/* Header */}
      <header className="sticky top-0 z-40 bg-navy-deep/95 backdrop-blur border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/logo.png" alt="PlayMaster IA" className="w-12 h-12 sm:w-14 sm:h-14" />
            <span className="font-h text-lg sm:text-xl uppercase tracking-widest">PlayMaster IA</span>
          </Link>
          <nav className="flex items-center gap-3 text-xs font-mono uppercase tracking-widest">
            <Link to={playsBase} className="text-slate-300 hover:text-gold">{t('ph_nav_jugadas')}</Link>
            <Link to="/coaches" className="text-slate-300 hover:text-gold">{t('ph_nav_coaches')}</Link>
            <Link to="/login" className="text-slate-300 hover:text-gold">{t('ph_nav_login')}</Link>
          </nav>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-10">
        {/* Hero */}
        <section className="flex flex-col sm:flex-row gap-6 items-start mb-10" data-testid="coach-hero">
          <div className="shrink-0">
            {coach.avatar_url ? (
              <img src={coach.avatar_url} alt={coach.name} className="w-28 h-28 rounded-2xl border-2 border-gold/40 object-cover" />
            ) : (
              <div className="w-28 h-28 rounded-2xl border-2 border-gold/40 bg-navy-deep flex items-center justify-center font-h text-4xl text-gold">
                {(coach.name || 'C').charAt(0)}
              </div>
            )}
          </div>
          <div className="flex-1">
            <div className="text-[10px] font-mono uppercase tracking-[0.4em] text-gold/80">{t('cp_label_coach')}</div>
            <h1 className="font-h text-4xl sm:text-5xl uppercase tracking-tight text-white mt-1">{coach.name}</h1>
            {coach.country && <div className="mt-1 text-sm text-slate-400 font-mono uppercase tracking-widest">{coach.country}</div>}
            {coach.bio && <p className="mt-4 text-base text-slate-300 max-w-2xl">{coach.bio}</p>}
            <div className="mt-4 flex flex-wrap gap-3 text-xs">
              {coach.website && (
                <a href={coach.website} target="_blank" rel="noopener noreferrer" data-testid="coach-link-website" className="inline-flex items-center gap-1.5 text-slate-300 hover:text-gold">
                  <Globe className="w-3.5 h-3.5" /> {t('ppe_website')}
                </a>
              )}
              {coach.twitter && (
                <a href={`https://twitter.com/${coach.twitter}`} target="_blank" rel="noopener noreferrer" data-testid="coach-link-twitter" className="inline-flex items-center gap-1.5 text-slate-300 hover:text-gold">
                  <Twitter className="w-3.5 h-3.5" /> @{coach.twitter}
                </a>
              )}
              {coach.instagram && (
                <a href={`https://instagram.com/${coach.instagram}`} target="_blank" rel="noopener noreferrer" data-testid="coach-link-instagram" className="inline-flex items-center gap-1.5 text-slate-300 hover:text-gold">
                  <Instagram className="w-3.5 h-3.5" /> @{coach.instagram}
                </a>
              )}
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="flex flex-wrap gap-3 mb-10" data-testid="coach-stats">
          <StatPill icon={BookOpen} label={t('cp_stat_plays')} value={fmt(coach.stats.plays, lang)} />
          <StatPill icon={Heart} label={t('cp_stat_likes')} value={fmt(coach.stats.likes, lang)} accent="text-rose-400" />
          <StatPill icon={Eye} label={t('cp_stat_views')} value={fmt(coach.stats.views, lang)} accent="text-sky-400" />
          {coach.stats.plays >= 10 && (
            <StatPill icon={Award} label={t('cp_stat_verified')} value="✓" accent="text-emerald-400" />
          )}
        </section>

        {/* Plays grid */}
        <section data-testid="coach-plays-grid">
          <h2 className="font-h text-2xl uppercase tracking-tight mb-4">{t('cp_his_plays')}</h2>
          {coach.plays.length === 0 ? (
            <div className="rounded-xl border border-gold/15 bg-navy-deep p-8 text-center text-slate-400">
              {t('cp_no_plays_yet')}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {coach.plays.map((p) => <PlayCard key={p.id} play={p} lang={lang} />)}
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
